import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../auth";

export interface UserPreferences {
  dietary: {
    vegan: boolean;
    vegetarian: boolean;
    glutenFree: boolean;
    none: boolean;
  };
  cookingTime: number;
  cuisines: string[];
  includedIngredients: string[];
  excludedIngredients: string[];
  duration: number;
  servings: number;
}

interface CustomError {
  message: string;
}

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user]);

  const loadUserPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select(
          "dietary, cooking_time, cuisines, included_ingredients, excluded_ingredients, duration, servings",
        )
        .eq("user_id", user?.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setPreferences({
          dietary: data.dietary,
          cookingTime: data.cooking_time,
          cuisines: data.cuisines,
          includedIngredients: data.included_ingredients,
          excludedIngredients: data.excluded_ingredients,
          duration: data.duration || 7,
          servings: data.servings || 4,
        });
      } else {
        setPreferences(null);
      }
    } catch (err: unknown) {
      const error = err as CustomError;
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveUserPreferences = async (newPreferences: UserPreferences) => {
    if (!user) return;

    try {
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: user.id,
          dietary: newPreferences.dietary,
          cooking_time: newPreferences.cookingTime,
          cuisines: newPreferences.cuisines,
          included_ingredients: newPreferences.includedIngredients,
          excluded_ingredients: newPreferences.excludedIngredients,
          duration: newPreferences.duration,
          servings: newPreferences.servings,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id",
        },
      );

      if (error) throw error;

      // Update local state
      setPreferences(newPreferences);
      return newPreferences;
    } catch (err: unknown) {
      const error = err as CustomError;
      setError(error.message);
      throw error;
    }
  };

  return {
    preferences,
    loading,
    error,
    saveUserPreferences,
    loadUserPreferences,
  };
}
